import React, {useEffect, useState} from "react"
import {
	Box,
	Chip,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Pagination,
	Select,
	Typography,
} from "@mui/material"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import {Link as RouterLink, useSearchParams} from "react-router-dom"
import styled from "styled-components"
import {differenceInMinutes, format} from "date-fns"

import {api, UnidentifiedInboxMessage} from "@indebted/api"
import {BackToSearch} from "@indebted/components/BackToSearch"
import {Layout} from "@indebted/components/Layout"
import {colors} from "@indebted/theme/colors"

const PAGE_SIZE = 20

const phrasingByDeferralReason: Record<string, string> = {
	NoMatch: "No matching customer",
	MultipleCandidates: "Several possible customers",
	AmbiguousSender: "Sender address is ambiguous",
}

type InboxState = {
	loading: boolean
	messages: UnidentifiedInboxMessage[]
	total: number
	mailboxes: string[]
	reasons: string[]
	error: string
}

const emptyInbox: InboxState = {loading: true, messages: [], total: 0, mailboxes: [], reasons: [], error: ""}

const DeferralReasonChip = styled(Chip).attrs({size: "small", variant: "outlined"})`
	&& {
		background-color: ${colors.warning["50"]};
		border-color: ${colors.warning["200"]};
		color: ${colors.warning["800"]};
	}
	font-weight: 500;
`

const InboxCard = styled(RouterLink)`
	display: block;
	border-radius: 8px;
	padding: 16px;
	background-color: ${colors.primary.white};
	border: 1px solid transparent;
	box-shadow:
		0px 3px 1px -2px rgba(0, 0, 0, 0.2),
		0px 2px 2px 0px rgba(0, 0, 0, 0.14),
		0px 1px 5px 0px rgba(0, 0, 0, 0.12);
	transition: box-shadow 0.2s;
	text-decoration: none;
	color: inherit;

	&:hover {
		box-shadow:
			0px 3px 3px -2px rgba(66, 103, 178, 0.2),
			0px 4px 6px 0px rgba(66, 103, 178, 0.14),
			0px 2px 8px 1px rgba(66, 103, 178, 0.12);
	}
`

function UnidentifiedInboxPage() {
	const [searchParams, setSearchParams] = useSearchParams()
	const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1)
	const mailbox = searchParams.get("mailbox") ?? ""
	const reason = searchParams.get("reason") ?? ""
	const [inbox, setInbox] = useState<InboxState>(emptyInbox)

	useEffect(() => {
		let showing = true
		setInbox((previous) => ({...previous, loading: true, error: ""}))
		api.inbox
			.listUnidentified({limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, mailbox, reason})
			.then((answer) => {
				if (!showing) return
				setInbox({
					loading: false,
					messages: answer.Messages,
					total: answer.Total,
					mailboxes: answer.Mailboxes,
					reasons: answer.DeferralReasons,
					error: "",
				})
			})
			.catch((failure) => {
				if (!showing) return
				setInbox((previous) => ({
					loading: false,
					messages: [],
					total: previous.total,
					mailboxes: previous.mailboxes,
					reasons: previous.reasons,
					error: failure?.Message ?? "The inbox could not be read",
				}))
			})

		return () => {
			showing = false
		}
	}, [page, mailbox, reason])

	const chooseFilter = (name: string, value: string) => {
		const next = new URLSearchParams(searchParams)
		if (value === "") {
			next.delete(name)
		} else {
			next.set(name, value)
		}
		next.delete("page")
		setSearchParams(next)
	}

	const setPage = (newPage: number) => {
		const next = new URLSearchParams(searchParams)
		if (newPage <= 1) {
			next.delete("page")
		} else {
			next.set("page", String(newPage))
		}
		setSearchParams(next)
		window.scrollTo({top: 0, behavior: "smooth"})
	}

	const totalPages = Math.max(1, Math.ceil(inbox.total / PAGE_SIZE))
	const pager = inbox.total > PAGE_SIZE && (
		<Pagination
			count={totalPages}
			page={Math.min(page, totalPages)}
			onChange={(_, value) => setPage(value)}
			color="primary"
			size="small"
		/>
	)

	return (
		<Layout>
			<Box px={3} pt={3} mb={2}>
				<BackToSearch backURL="/" />
			</Box>
			<Box px={3} pb={4} maxWidth={880}>
				<Typography variant="h6" mb={2}>
					Unidentified inbox
				</Typography>

				<Box display="flex" gap={2} mb={2} flexWrap="wrap">
					<FormControl size="small" sx={{minWidth: 240}}>
						<InputLabel shrink id="inbox-mailbox-filter">
							Mailbox
						</InputLabel>
						<Select
							labelId="inbox-mailbox-filter"
							label="Mailbox"
							displayEmpty
							value={mailbox}
							onChange={(event) => chooseFilter("mailbox", event.target.value)}
						>
							<MenuItem value="">All mailboxes</MenuItem>
							{inbox.mailboxes.map((named) => (
								<MenuItem key={named} value={named}>
									{named}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{minWidth: 240}}>
						<InputLabel shrink id="inbox-reason-filter">
							Reason
						</InputLabel>
						<Select
							labelId="inbox-reason-filter"
							label="Reason"
							displayEmpty
							value={reason}
							onChange={(event) => chooseFilter("reason", event.target.value)}
						>
							<MenuItem value="">All reasons</MenuItem>
							{inbox.reasons.map((named) => (
								<MenuItem key={named} value={named}>
									{phraseDeferralReason(named)}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>

				{inbox.loading && <CircularProgress size={24} />}

				{!inbox.loading && inbox.error !== "" && <Typography color="error">{inbox.error}</Typography>}

				{!inbox.loading && inbox.error === "" && inbox.total > 0 && (
					<Typography color="text.secondary" mb={1}>
						{inbox.total} waiting for identification
					</Typography>
				)}

				{!inbox.loading && <Box mb={1}>{pager}</Box>}

				{!inbox.loading && inbox.error === "" && inbox.messages.length === 0 && (
					<Typography color="text.secondary">
						{inbox.total === 0 ? "No messages are waiting for identification" : "No messages on this page"}
					</Typography>
				)}

				<Box display="flex" flexDirection="column" gap={1}>
					{!inbox.loading &&
						inbox.error === "" &&
						inbox.messages.map((message) => (
							<InboxCard
								key={`${message.ConversationID}:${message.ProviderMessageID}`}
								to={itemView(message)}
							>
								<Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
									<DeferralReasonChip label={phraseDeferralReason(message.DeferralReason)} />
									{message.Mailbox !== "" && (
										<Chip size="small" variant="outlined" label={message.Mailbox} />
									)}
									<Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
										<AccessTimeIcon fontSize="small" />
										<Typography
											variant="body2"
											color="text.secondary"
											title={format(new Date(message.DeferredAt), "d MMM yyyy HH:mm")}
										>
											Waiting {phraseWait(message.DeferredAt)}
										</Typography>
									</Box>
								</Box>
								<Typography variant="body2" color="text.secondary" mb={0.5}>
									{sender(message)}
								</Typography>
								<Typography variant="subtitle1" fontWeight={600}>
									{message.Subject || "(no subject)"}
								</Typography>
							</InboxCard>
						))}
				</Box>

				{!inbox.loading && <Box mt={2}>{pager}</Box>}
			</Box>
		</Layout>
	)
}

function itemView(message: UnidentifiedInboxMessage) {
	// The list serves email only, so the channel segment is fixed until SMS gets its own inbox.
	return `/inbox/email/${message.ConversationID}/${encodeURIComponent(message.ProviderMessageID)}`
}

// The queue is read from its oldest end, where a fuzzed duration stops
// discriminating: two entries fifty and fifty-nine hours old both read "2 days".
// Days carry their spare hours so the head of the queue stays ordered to the eye.
function phraseWait(deferredAt: string) {
	const minutes = Math.max(0, differenceInMinutes(new Date(), new Date(deferredAt)))
	if (minutes < 60) return counted(minutes, "minute")

	const hours = Math.floor(minutes / 60)
	if (hours < 24) return counted(hours, "hour")

	const days = Math.floor(hours / 24)
	const spareHours = hours % 24

	return spareHours === 0 ? counted(days, "day") : `${counted(days, "day")} ${counted(spareHours, "hour")}`
}

function counted(n: number, noun: string) {
	return `${n} ${noun}${n === 1 ? "" : "s"}`
}

function sender(message: UnidentifiedInboxMessage) {
	return message.FromLabel === "" ? message.FromAddress : `${message.FromLabel} <${message.FromAddress}>`
}

function phraseDeferralReason(reason: string) {
	return phrasingByDeferralReason[reason] ?? reason
}

export {UnidentifiedInboxPage}
